import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiMail, FiPhone, FiBriefcase, FiCalendar, FiEdit, FiGrid, FiList, FiImage } from 'react-icons/fi';
import { HiOutlineBuildingOffice2 } from 'react-icons/hi2';
import { MdVerified } from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import { usersAPI, postsAPI } from '../../services/api';
import PostCard from '../../components/PostCard/PostCard';
import PostListView from '../../components/PostListView/PostListView';
import PostGalleryView from '../../components/PostGalleryView/PostGalleryView';
import VideoCard from '../../components/VideoCard/VideoCard';
import MemeCard from '../../components/MemeCard/MemeCard';
import toast from 'react-hot-toast';
import { resolveMediaUrl } from '../../utils/media';
import './Profile.css';
import VerificationRequestModal from '../../components/VerificationRequestModal/VerificationRequestModal';

const Profile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const normalizedUsername = typeof username === 'string' ? username.replace(/^@/, '') : username;
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // all, articles, videos, memes
  const [activeView, setActiveView] = useState('card'); // card, list, gallery
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalArticles: 0,
    totalVideos: 0,
    totalMemes: 0,
    totalLikes: 0,
    totalComments: 0
  });

  const [verificationRequests, setVerificationRequests] = useState([]);
  const [verificationForm, setVerificationForm] = useState({ requested_level: 'basic', notes: '' });
  const [verificationSubmitting, setVerificationSubmitting] = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);

  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);

  const [activeList, setActiveList] = useState(null); // null | 'followers' | 'following' | 'likes'
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [listUsers, setListUsers] = useState([]);
  const [listPosts, setListPosts] = useState([]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        
        // If no username in URL, show current user's profile
        if (!normalizedUsername) {
          if (currentUser) {
            setIsOwnProfile(true);
            // Load full profile from API to get counts/badges
            const identifier = currentUser.username || String(currentUser.id);
            const response = await usersAPI.getProfileByUsername(identifier);
            setUser(response.data);
            setFollowersCount(response.data?.followers_count || 0);
            setFollowingCount(response.data?.following_count || 0);
          }
          setLoading(false);
          return;
        }

        // Check if viewing own profile by username
        if (
          currentUser &&
          typeof currentUser.username === 'string' &&
          normalizedUsername.toLowerCase() === currentUser.username.toLowerCase()
        ) {
          setIsOwnProfile(true);
          const identifier = currentUser.username || String(currentUser.id);
          const response = await usersAPI.getProfileByUsername(identifier);
          setUser(response.data);
          setFollowersCount(response.data?.followers_count || 0);
          setFollowingCount(response.data?.following_count || 0);
          setLoading(false);
          return;
        }

        // Check if viewing own profile by ID
        if (currentUser && normalizedUsername === String(currentUser.id)) {
          setIsOwnProfile(true);
          const identifier = currentUser.username || String(currentUser.id);
          const response = await usersAPI.getProfileByUsername(identifier);
          setUser(response.data);
          setFollowersCount(response.data?.followers_count || 0);
          setFollowingCount(response.data?.following_count || 0);
          setLoading(false);
          return;
        }

        // Load other user's profile from API (supports both username and ID)
        const response = await usersAPI.getProfileByUsername(normalizedUsername);
        setUser(response.data);
        setFollowersCount(response.data?.followers_count || 0);
        setFollowingCount(response.data?.following_count || 0);
        
        // Check if it's actually our own profile after loading
        setIsOwnProfile(currentUser && Number(response.data.id) === Number(currentUser.id));
      } catch (error) {
        console.error('Error loading profile:', error);
        if (error.response?.status === 404) {
          toast.error('Usuario no encontrado');
        } else {
          toast.error('Error al cargar el perfil');
        }
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [normalizedUsername, currentUser]);

  useEffect(() => {
    if (!currentUser?.id || !user?.id) return;
    setIsOwnProfile(Number(currentUser.id) === Number(user.id));
  }, [currentUser, user]);

  useEffect(() => {
    const loadFollowStatus = async () => {
      if (!user?.id || isOwnProfile || !isAuthenticated) {
        setIsFollowing(false);
        return;
      }
      try {
        const identifier = user.username || String(user.id);
        const res = await usersAPI.getFollowStatus(identifier);
        setIsFollowing(!!res.data?.isFollowing);
        if (typeof res.data?.followers_count === 'number') setFollowersCount(res.data.followers_count);
        if (typeof res.data?.following_count === 'number') setFollowingCount(res.data.following_count);
      } catch (e) {
        // Silent: profile should still render
      }
    };

    loadFollowStatus();
  }, [user, isOwnProfile, isAuthenticated]);

  const handleToggleFollow = async () => {
    if (!user?.id) return;
    if (currentUser && Number(user.id) === Number(currentUser.id)) return;
    if (!isAuthenticated) {
      toast.error('Inicia sesión para seguir usuarios');
      navigate('/login');
      return;
    }
    if (isOwnProfile) return;

    setFollowLoading(true);
    try {
      const identifier = user.username || String(user.id);
      const res = isFollowing
        ? await usersAPI.unfollow(identifier)
        : await usersAPI.follow(identifier);
      setIsFollowing(!!res.data?.isFollowing);
      if (typeof res.data?.followers_count === 'number') setFollowersCount(res.data.followers_count);
      if (typeof res.data?.following_count === 'number') setFollowingCount(res.data.following_count);
    } catch (error) {
      console.error('Error toggling follow:', error);
      toast.error('No se pudo actualizar el seguimiento');
    } finally {
      setFollowLoading(false);
    }
  };

  useEffect(() => {
    const loadMyVerificationRequests = async () => {
      if (!isOwnProfile) return;
      try {
        const res = await usersAPI.getVerificationRequests();
        setVerificationRequests(res.data || []);
      } catch (e) {
        // Silent: profile should still render
      }
    };

    loadMyVerificationRequests();
  }, [isOwnProfile]);

  const submitVerificationRequest = async () => {
    setVerificationSubmitting(true);
    try {
      await usersAPI.requestVerification({
        requested_level: verificationForm.requested_level,
        notes: verificationForm.notes
      });
      toast.success('Solicitud enviada al equipo de soporte');
      const res = await usersAPI.getVerificationRequests();
      setVerificationRequests(res.data || []);
      setVerificationForm((p) => ({ ...p, notes: '' }));
    } catch (error) {
      const msg = error?.response?.data?.error;
      if (error?.response?.status === 409) {
        toast.error(msg || 'Ya tienes una solicitud pendiente');
      } else {
        toast.error(msg || 'No se pudo enviar la solicitud');
      }
    } finally {
      setVerificationSubmitting(false);
    }
  };

  const loadUserPosts = useCallback(async () => {
    try {
      setLoadingPosts(true);
      
      let filters = { author: user.id };
      
      // Apply tab filter
      if (activeTab === 'articles') {
        filters.type = 'article';
      } else if (activeTab === 'videos') {
        filters.type = ['video-horizontal', 'video-reel'];
      } else if (activeTab === 'memes') {
        filters.type = 'meme';
      }
      
      const response = await postsAPI.getAll(filters);
      const userPosts = response.data.posts || [];
      setPosts(userPosts);
      
      // Calculate stats
      const totalArticles = userPosts.filter(p => p.type === 'article').length;
      const totalVideos = userPosts.filter(p => p.type === 'video-horizontal' || p.type === 'video-reel').length;
      const totalMemes = userPosts.filter(p => p.type === 'meme').length;
      const totalLikes = userPosts.reduce((sum, post) => sum + (post.likes_count || 0), 0);
      const totalComments = userPosts.reduce((sum, post) => sum + (post.comments_count || 0), 0);
      
      setStats({
        totalPosts: userPosts.length,
        totalArticles,
        totalVideos,
        totalMemes,
        totalLikes,
        totalComments
      });
    } catch (error) {
      console.error('Error loading posts:', error);
      toast.error('Error al cargar las publicaciones');
    } finally {
      setLoadingPosts(false);
    }
  }, [user, activeTab]);

  const getIdentifier = useCallback(() => {
    if (!user) return null;
    return user.username || String(user.id);
  }, [user]);

  const getPostLink = (post) => {
    if (!post) return '/';
    if (post.type === 'meme') return `/meme/${post.slug || post.id}`;
    if (post.type === 'video-horizontal' || post.type === 'video-reel') return `/video/${post.slug || post.id}`;
    return `/article/${post.slug || post.id}`;
  };

  const openList = async (type) => {
    const identifier = getIdentifier();
    if (!identifier) {
      toast.error('No se pudo identificar el usuario');
      return;
    }

    setActiveList(type);
    setListUsers([]);
    setListPosts([]);
    setListError(null);
    setListLoading(true);

    try {
      if (type === 'followers') {
        const res = await usersAPI.getFollowers(identifier);
        setListUsers(res.data?.users || []);
      } else if (type === 'following') {
        const res = await usersAPI.getFollowing(identifier);
        setListUsers(res.data?.users || []);
      } else if (type === 'likes') {
        const res = await usersAPI.getLikes(identifier);
        setListPosts(res.data?.posts || []);
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        setListError('Esta lista es privada');
      } else {
        setListError('No se pudo cargar la lista');
      }
    } finally {
      setListLoading(false);
    }
  };

  // Load posts when user is loaded or tab changes
  useEffect(() => {
    if (user && user.id) {
      loadUserPosts();
    }
  }, [user, activeTab, loadUserPosts]);

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
        <p>Cargando perfil...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-not-found">
        <h2>Usuario no encontrado</h2>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          Volver al inicio
        </button>
      </div>
    );
  }

  const renderBadge = () => {
    if (!user.badge_color) return null;
    
    return (
      <div 
        className="verification-badge" 
        style={{ backgroundColor: user.badge_color }}
        title={user.badge_label || 'Cuenta verificada'}
      >
        <MdVerified />
      </div>
    );
  };

  const canRequestVerification = isOwnProfile && (user.verification_level === 'none' || !user.badge_color);
  const latestVerificationRequest = verificationRequests?.[0] || null;

  const renderPosts = () => {
    if (loadingPosts) {
      return (
        <div className="posts-loading">
          <div className="spinner"></div>
          <p>Cargando publicaciones...</p>
        </div>
      );
    }

    if (posts.length === 0) {
      return (
        <div className="no-posts">
          <p>
            {isOwnProfile 
              ? 'Aún no has publicado nada. ¡Crea tu primera publicación!' 
              : 'Este usuario no tiene publicaciones todavía.'}
          </p>
          {isOwnProfile && (
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/create')}
            >
              Crear Publicación
            </button>
          )}
        </div>
      );
    }

    // Render based on view type
    if (activeView === 'list') {
      return (
        <div className="posts-list-view">
          {posts.map(post => (
            <PostListView key={post.id} post={post} />
          ))}
        </div>
      );
    } else if (activeView === 'gallery') {
      return (
        <div className="posts-gallery-view">
          {posts.map(post => (
            <PostGalleryView key={post.id} post={post} />
          ))}
        </div>
      );
    } else {
      // Card view - render different components based on post type
      return (
        <div className="posts-grid">
          {posts.map(post => {
            if (post.type === 'meme') {
              return <MemeCard key={post.id} post={post} />;
            } else if (post.type === 'video-horizontal' || post.type === 'video-reel') {
              return <VideoCard key={post.id} post={post} />;
            } else {
              return <PostCard key={post.id} post={post} />;
            }
          })}
        </div>
      );
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* Header */}
        <div className="profile-header">
          <div className="profile-avatar-section">
            {user.avatar ? (
              <img src={resolveMediaUrl(user.avatar)} alt={user.name} className="profile-avatar" />
            ) : (
              <div className="profile-avatar-placeholder">
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
            {renderBadge()}

            {canRequestVerification && (
              <div className="profile-verify-cta">
                <div className="profile-verify-text">
                  <span className="profile-verify-title">Cuenta sin verificar</span>
                  <span className="profile-verify-subtitle">Solicita tu verificación para obtener el chulo</span>
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setVerificationModalOpen(true)}
                >
                  Solicitar verificación
                </button>
              </div>
            )}
          </div>

          <div className="profile-info-section">
            <div className="profile-title">
              <h1>{user.name}</h1>
              <p className="profile-username">@{user.username || 'usuario'}</p>
              {user.role_label && user.role_label !== 'Usuario' && (
                <span className="profile-role">{user.role_label}</span>
              )}
            </div>

            {/* Stats */}
            <div className="profile-stats">
              <div className="stat-item">
                <span className="stat-value">{stats.totalPosts}</span>
                <span className="stat-label">Publicaciones</span>
              </div>
              <button type="button" className="stat-item stat-item-clickable" onClick={() => openList('followers')}>
                <span className="stat-value">{followersCount}</span>
                <span className="stat-label">Seguidores</span>
              </button>
              <button type="button" className="stat-item stat-item-clickable" onClick={() => openList('following')}>
                <span className="stat-value">{followingCount}</span>
                <span className="stat-label">Siguiendo</span>
              </button>
              <button
                type="button"
                className="stat-item stat-item-clickable"
                onClick={() => openList('likes')}
              >
                <span className="stat-value">{typeof user.liked_posts_count === 'number' ? user.liked_posts_count : stats.totalLikes}</span>
                <span className="stat-label">Me gusta</span>
              </button>
              <div className="stat-item">
                <span className="stat-value">{stats.totalComments}</span>
                <span className="stat-label">Comentarios</span>
              </div>
            </div>

            {isOwnProfile ? (
              <button 
                className="btn-edit-profile"
                onClick={() => navigate('/settings/profile')}
              >
                <FiEdit />
                Editar Perfil
              </button>
            ) : (
              <button
                className="btn-edit-profile"
                onClick={handleToggleFollow}
                disabled={followLoading}
              >
                {followLoading ? '...' : isFollowing ? 'Siguiendo' : 'Seguir'}
              </button>
            )}
          </div>
        </div>

        {/* Bio */}
        {user.bio && (
          <div className="profile-bio-section">
            <p className="profile-bio">{user.bio}</p>
          </div>
        )}

        {/* Tabs for content type */}
        <div className="profile-content-section">
          <div className="profile-tabs">
            <button 
              className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              <span>Todas</span>
              <span className="tab-count">{stats.totalPosts}</span>
            </button>
            <button 
              className={`tab-button ${activeTab === 'articles' ? 'active' : ''}`}
              onClick={() => setActiveTab('articles')}
            >
              <span>Artículos</span>
              <span className="tab-count">{stats.totalArticles}</span>
            </button>
            <button 
              className={`tab-button ${activeTab === 'videos' ? 'active' : ''}`}
              onClick={() => setActiveTab('videos')}
            >
              <span>Videos</span>
              <span className="tab-count">{stats.totalVideos}</span>
            </button>
            <button 
              className={`tab-button ${activeTab === 'memes' ? 'active' : ''}`}
              onClick={() => setActiveTab('memes')}
            >
              <span>Memes</span>
              <span className="tab-count">{stats.totalMemes}</span>
            </button>
          </div>

          {/* View toggle */}
          <div className="view-toggle">
            <button
              className={`view-btn ${activeView === 'card' ? 'active' : ''}`}
              onClick={() => setActiveView('card')}
              title="Vista de tarjetas"
            >
              <FiGrid />
            </button>
            <button
              className={`view-btn ${activeView === 'list' ? 'active' : ''}`}
              onClick={() => setActiveView('list')}
              title="Vista de lista"
            >
              <FiList />
            </button>
            <button
              className={`view-btn ${activeView === 'gallery' ? 'active' : ''}`}
              onClick={() => setActiveView('gallery')}
              title="Vista de galería"
            >
              <FiImage />
            </button>
          </div>
        </div>

        {/* Posts */}
        <div className="profile-posts">
          {renderPosts()}
        </div>

        {/* Details (collapsed section) */}
        {isOwnProfile && (
          <details className="profile-details-section">
            <summary>Información Personal</summary>
            <div className="profile-details">
              {user.email && (
                <div className="profile-detail-item">
                  <FiMail className="detail-icon" />
                  <div>
                    <label>Email</label>
                    <p>{user.email}</p>
                    {user.email_verified && (
                      <span className="verified-tag">Verificado</span>
                    )}
                  </div>
                </div>
              )}

              {user.phone && (
                <div className="profile-detail-item">
                  <FiPhone className="detail-icon" />
                  <div>
                    <label>Teléfono</label>
                    <p>{user.phone}</p>
                  </div>
                </div>
              )}

              {user.institution && (
                <div className="profile-detail-item">
                  <HiOutlineBuildingOffice2 className="detail-icon" />
                  <div>
                    <label>Institución</label>
                    <p>{user.institution}</p>
                  </div>
                </div>
              )}

              {user.occupation && (
                <div className="profile-detail-item">
                  <FiBriefcase className="detail-icon" />
                  <div>
                    <label>Ocupación</label>
                    <p>{user.occupation}</p>
                  </div>
                </div>
              )}

              {user.affiliation && (
                <div className="profile-detail-item">
                  <HiOutlineBuildingOffice2 className="detail-icon" />
                  <div>
                    <label>Afiliación</label>
                    <p>{user.affiliation}</p>
                  </div>
                </div>
              )}

              {user.created_at && (
                <div className="profile-detail-item">
                  <FiCalendar className="detail-icon" />
                  <div>
                    <label>Miembro desde</label>
                    <p>{new Date(user.created_at).toLocaleDateString('es-ES', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</p>
                  </div>
                </div>
              )}
            </div>
          </details>
        )}

        {activeList && (
          <div className="profile-list-modal-overlay" onClick={() => setActiveList(null)}>
            <div className="profile-list-modal" onClick={(e) => e.stopPropagation()}>
              <div className="profile-list-modal-header">
                <h3>
                  {activeList === 'followers'
                    ? 'Seguidores'
                    : activeList === 'following'
                      ? 'Siguiendo'
                      : 'Me gusta'}
                </h3>
                <button type="button" className="profile-list-modal-close" onClick={() => setActiveList(null)}>
                  ×
                </button>
              </div>

              {listLoading ? (
                <div className="profile-lists-loading">
                  <div className="spinner"></div>
                  <p>Cargando...</p>
                </div>
              ) : listError ? (
                <div className="profile-lists-content">
                  <p className="profile-lists-empty">{listError}</p>
                </div>
              ) : activeList === 'likes' ? (
                <div className="profile-lists-content">
                  {listPosts.length === 0 ? (
                    <p className="profile-lists-empty">No hay publicaciones con Me gusta.</p>
                  ) : (
                    <div className="profile-likes-list">
                      {listPosts.map((post) => (
                        <button
                          key={post.id}
                          type="button"
                          className="profile-like-item"
                          onClick={() => {
                            setActiveList(null);
                            navigate(getPostLink(post));
                          }}
                        >
                          <div className="profile-like-thumb">
                            {(post.image || post.video_url) ? (
                              <img
                                src={resolveMediaUrl(post.image || post.video_url)}
                                alt={post.title}
                              />
                            ) : (
                              <div className="profile-like-thumb-placeholder">{(post.title || 'P').charAt(0).toUpperCase()}</div>
                            )}
                          </div>
                          <div className="profile-like-info">
                            <div className="profile-like-title">{post.title}</div>
                            <div className="profile-like-meta">
                              {post.type === 'meme' ? 'Meme' : (post.type === 'video-horizontal' || post.type === 'video-reel') ? 'Video' : 'Artículo'}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="profile-lists-content">
                  {listUsers.length === 0 ? (
                    <p className="profile-lists-empty">No hay usuarios para mostrar.</p>
                  ) : (
                    <div className="profile-users-list">
                      {listUsers.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          className="profile-user-item"
                          onClick={() => {
                            setActiveList(null);
                            navigate(`/${u.username || u.id}`);
                          }}
                        >
                          {u.avatar ? (
                            <img className="profile-user-avatar" src={resolveMediaUrl(u.avatar)} alt={u.name} />
                          ) : (
                            <div className="profile-user-avatar-placeholder">{(u.name || 'U').charAt(0).toUpperCase()}</div>
                          )}
                          <div className="profile-user-info">
                            <div className="profile-user-name">
                              {u.name}
                              {u.badge_color && <MdVerified size={16} style={{ color: u.badge_color, marginLeft: 6 }} />}
                            </div>
                            <div className="profile-user-username">@{u.username || 'usuario'}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {canRequestVerification && (
          <VerificationRequestModal
            open={verificationModalOpen}
            onClose={() => setVerificationModalOpen(false)}
            latestRequest={latestVerificationRequest}
            form={verificationForm}
            setForm={setVerificationForm}
            onSubmit={submitVerificationRequest}
            submitting={verificationSubmitting}
          />
        )}

      </div>
    </div>
  );
};

export default Profile;
